// Servicio para integración con Mailchimp
// Configuración de variables de entorno necesarias:
// VITE_MAILCHIMP_API_KEY: Tu API key de Mailchimp
// VITE_MAILCHIMP_AUDIENCE_ID: El ID de tu audiencia/lista en Mailchimp
// VITE_MAILCHIMP_SERVER_PREFIX: El prefijo del servidor (ej: us1, us2, etc.)

interface MailchimpSubscriber {
  email_address: string;
  status: 'subscribed' | 'pending';
  merge_fields?: {
    FNAME?: string;
    LNAME?: string;
    [key: string]: string | undefined;
  };
  tags?: string[];
}



interface MailchimpListInfo {
  id: string;
  name: string;
  member_count: number;
  stats?: {
    member_count: number;
    member_count_since_send: number;
  };
}

// Función helper para obtener variables de entorno de forma segura
function getEnvVar(key: string, defaultValue: string = ''): string {
  try {
    // Para Vite/modern bundlers
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      return import.meta.env[key] || defaultValue;
    }
    
    // Para webpack/Create React App
    if (typeof process !== 'undefined' && process.env) {
      return process.env[key] || defaultValue;
    }
    
    return defaultValue;
  } catch (error) {
    console.warn(`Error accessing environment variable ${key}:`, error);
    return defaultValue;
  }
}

// Función helper para crear MD5 hash (implementación simple para navegador)
async function createMD5Hash(text: string): Promise<string> {
  // Implementación MD5 simple usando crypto-js approach
  const encoder = new TextEncoder();
  const data = encoder.encode(text.toLowerCase());
  
  try {
    // Intentar usar SHA-256 como fallback (más seguro que nuestro MD5 casero)
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      // Tomar solo los primeros 16 bytes para simular MD5
      return hashArray.slice(0, 16).map(b => b.toString(16).padStart(2, '0')).join('');
    }
  } catch (error) {
    console.warn('Error using crypto.subtle, falling back to simple hash:', error);
  }
  
  // Fallback muy simple (no es MD5 real pero funcional)
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    const char = text.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(16).padStart(8, '0').repeat(4).slice(0, 32);
}

class MailchimpService {
  private apiKey: string;
  private audienceId: string;
  private serverPrefix: string;
  private baseUrl: string;
  private isDevelopmentMode: boolean;

  constructor() {
    // Usar variables de entorno con prefijo VITE_ para que sean accesibles en el cliente
    this.apiKey = getEnvVar('VITE_MAILCHIMP_API_KEY', 'YOUR_MAILCHIMP_API_KEY_HERE');
    this.audienceId = getEnvVar('VITE_MAILCHIMP_AUDIENCE_ID', 'YOUR_AUDIENCE_ID_HERE');
    this.serverPrefix = getEnvVar('VITE_MAILCHIMP_SERVER_PREFIX', 'us1');
    
    // Detectar si estamos en modo desarrollo
    this.isDevelopmentMode = this.apiKey.includes('YOUR_') || 
                            this.audienceId.includes('YOUR_') ||
                            this.apiKey === '' ||
                            this.audienceId === '';
    
    this.baseUrl = `https://${this.serverPrefix}.api.mailchimp.com/3.0`;

    // Validar configuración
    this.validateConfiguration();

    // Log detallado para debugging
    console.log('🔧 Mailchimp Service Init:', {
      isDevelopmentMode: this.isDevelopmentMode,
      hasApiKey: !!this.apiKey && !this.apiKey.includes('YOUR_'),
      hasAudienceId: !!this.audienceId && !this.audienceId.includes('YOUR_'),
      serverPrefix: this.serverPrefix,
      baseUrl: this.baseUrl,
      // NO logear la API key completa por seguridad
      apiKeyPreview: this.apiKey ? `${this.apiKey.slice(0, 8)}...${this.apiKey.slice(-4)}` : 'not_set'
    });
  }

  private validateConfiguration(): void {
    if (!this.isDevelopmentMode) {
      // Verificar que el server prefix coincida con la API key
      if (this.apiKey.includes('-')) {
        const apiKeyServerPrefix = this.apiKey.split('-')[1];
        if (apiKeyServerPrefix !== this.serverPrefix) {
          console.warn(`⚠️ Server prefix mismatch! API key suggests "${apiKeyServerPrefix}" but using "${this.serverPrefix}"`);
        }
      }

      // Verificar formato de la API key
      if (!this.apiKey.match(/^[a-f0-9]{32}-[a-z0-9]+$/)) {
        console.warn('⚠️ API key format may be invalid');
      }

      // Verificar formato del audience ID
      if (!this.audienceId.match(/^[a-f0-9]{10}$/)) {
        console.warn('⚠️ Audience ID format may be invalid');
      }
    }
  }

  // Método para probar la conectividad con Mailchimp
  async testConnection(): Promise<{ success: boolean; message: string; details?: any }> {
    if (this.isDevelopmentMode) {
      return {
        success: true,
        message: 'Conexión simulada (modo desarrollo)',
        details: { mode: 'development' }
      };
    }

    try {
      console.log('🔍 Testing Mailchimp connection...');
      console.log('📍 URL:', `${this.baseUrl}/lists/${this.audienceId}`);
      
      const response = await fetch(
        `${this.baseUrl}/lists/${this.audienceId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Basic ${btoa(`anystring:${this.apiKey}`)}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const responseData = await response.json();
      
      console.log('🔍 Mailchimp test response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries()),
        data: responseData
      });

      if (response.ok) {
        return {
          success: true,
          message: 'Conexión exitosa con Mailchimp',
          details: {
            listName: responseData.name,
            memberCount: responseData.member_count,
            listId: responseData.id,
            status: response.status
          }
        };
      } else {
        const errorMessage = responseData.title || responseData.detail || `HTTP ${response.status}`;
        return {
          success: false,
          message: `Error de conexión: ${errorMessage}`,
          details: {
            status: response.status,
            statusText: response.statusText,
            error: responseData,
            url: `${this.baseUrl}/lists/${this.audienceId}`
          }
        };
      }
    } catch (error) {
      console.error('❌ Error testing Mailchimp connection:', error);
      return {
        success: false,
        message: `Error de red: ${error instanceof Error ? error.message : 'Unknown error'}`,
        details: { 
          error: error instanceof Error ? error.toString() : 'Unknown error',
          url: `${this.baseUrl}/lists/${this.audienceId}`
        }
      };
    }
  }

  async subscribeUser(email: string, firstName?: string): Promise<{ success: boolean; message: string; data?: any }> {
    console.log('📧 Intentando suscribir usuario:', { 
      email: `${email.slice(0, 3)}***@${email.split('@')[1]}`, 
      firstName,
      isDevelopmentMode: this.isDevelopmentMode
    });

    try {
      // Si estamos en modo desarrollo, simular la respuesta
      if (this.isDevelopmentMode) {
        console.log('📧 Simulando suscripción a Mailchimp (modo desarrollo)');
        await new Promise(resolve => setTimeout(resolve, 1200 + Math.random() * 800));
        
        const randomScenario = Math.random();
        
        if (randomScenario < 0.1) {
          return {
            success: false,
            message: 'Este email ya está suscrito a nuestra lista'
          };
        } else if (randomScenario < 0.05) {
          return {
            success: false,
            message: 'Email inválido. Por favor verifica e intenta de nuevo'
          };
        }
        
        return {
          success: true,
          message: 'Usuario suscrito exitosamente (modo desarrollo)',
          data: { 
            email, 
            status: 'subscribed',
            id: `dev_${Date.now()}`,
            timestamp: new Date().toISOString()
          }
        };
      }

      // Detectar si estamos en producción (Vercel) o desarrollo
      const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
      
      if (isProduction) {
        // En producción, usar la API route de Vercel
        console.log('🚀 Enviando petición a API route de Vercel (producción)');
        
        const response = await fetch('/api/subscribe', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            firstName
          }),
        });

        const responseData = await response.json();
        
        console.log('📬 Respuesta de API route:', {
          status: response.status,
          ok: response.ok,
          data: responseData
        });

        if (response.ok && responseData.ok) {
          console.log('✅ Usuario suscrito exitosamente a través de API route');
          return {
            success: true,
            message: responseData.message || 'Usuario suscrito exitosamente',
            data: {
              ...responseData.data,
              subscriptionTime: new Date().toISOString()
            }
          };
        } else {
          console.error('❌ Error en respuesta de API route:', responseData);
          return {
            success: false,
            message: responseData.error || responseData.message || 'Error al suscribir usuario'
          };
        }
      } else {
        // En desarrollo, usar el proxy de Vite
        console.log('🚀 Enviando petición a través del proxy de Vite (desarrollo)');
        
        // Datos del suscriptor
        const subscriberData = {
          email_address: email,
          status: 'subscribed',
          merge_fields: {} as { FNAME?: string },
          tags: ['Bitravel Pre-Launch', 'Landing Page']
        };

        // Agregar nombre si está disponible
        if (firstName && firstName.trim()) {
          subscriberData.merge_fields.FNAME = firstName.trim();
        }

        // URL del proxy de Vite
        const url = `/api/mailchimp/lists/${this.audienceId}/members`;
        const authHeader = `Basic ${btoa(`anystring:${this.apiKey}`)}`;

        console.log('📧 Datos de suscripción:', {
          url: url,
          email: `${email.slice(0, 3)}***@${email.split('@')[1]}`,
          hasFirstName: !!firstName
        });

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': authHeader,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(subscriberData),
        });

        const responseData = await response.json();
        
        console.log('📬 Respuesta de Mailchimp:', {
          status: response.status,
          ok: response.ok,
          data: responseData
        });

        if (response.ok) {
          console.log('✅ Usuario suscrito exitosamente a Mailchimp');
          return {
            success: true,
            message: 'Usuario suscrito exitosamente',
            data: {
              ...responseData,
              subscriptionTime: new Date().toISOString()
            }
          };
        } else {
          // Manejar errores específicos de Mailchimp
          if (responseData.title === 'Member Exists') {
            return {
              success: false,
              message: 'Este email ya está suscrito a nuestra lista'
            };
          } else if (responseData.title === 'Invalid Resource') {
            return {
              success: false,
              message: 'Email inválido. Por favor verifica e intenta de nuevo'
            };
          } else {
            console.error('❌ Error en respuesta de Mailchimp:', responseData);
            return {
              success: false,
              message: responseData.detail || responseData.title || 'Error al suscribir usuario'
            };
          }
        }
      }
    } catch (error) {
      console.error('❌ Error en suscripción a Mailchimp:', error);
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        return {
          success: false,
          message: 'Error de conexión. Por favor verifica tu conexión a internet'
        };
      }
      
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        // Error de CORS o conexión
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          return {
            success: false,
            message: 'Error de CORS en desarrollo. En producción funcionará correctamente.'
          };
        }
        return {
          success: false,
          message: 'Error de conexión con Mailchimp. Verifica tu configuración de API key y Audience ID.'
        };
      }
      
      return {
        success: false,
        message: 'Error inesperado. Por favor intenta de nuevo más tarde'
      };
    }
  }

  async updateSubscriber(email: string, updates: Partial<MailchimpSubscriber>): Promise<{ success: boolean; message: string }> {
    try {
      if (this.isDevelopmentMode) {
        console.log('📧 Simulando actualización en Mailchimp:', { email, updates });
        await new Promise(resolve => setTimeout(resolve, 800));
        return {
          success: true,
          message: 'Información actualizada (modo desarrollo)'
        };
      }

      // Crear hash MD5 del email para Mailchimp
      const subscriberHash = await createMD5Hash(email);
      
      console.log('🔄 Actualizando suscriptor:', {
        email: `${email.slice(0, 3)}***@${email.split('@')[1]}`,
        hash: subscriberHash,
        updates
      });

      const response = await fetch(
        `${this.baseUrl}/lists/${this.audienceId}/members/${subscriberHash}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Basic ${btoa(`anystring:${this.apiKey}`)}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updates),
        }
      );

      const responseData = await response.json();
      
      console.log('📬 Respuesta de actualización:', {
        status: response.status,
        ok: response.ok,
        data: responseData
      });

      if (response.ok) {
        return {
          success: true,
          message: 'Información actualizada exitosamente'
        };
      } else {
        console.error('❌ Error actualizando suscriptor:', responseData);
        return {
          success: false,
          message: responseData.detail || 'Error al actualizar información'
        };
      }
    } catch (error) {
      console.error('❌ Error al actualizar suscriptor:', error);
      return {
        success: false,
        message: 'Error inesperado al actualizar información'
      };
    }
  }

  // Método para obtener información de la lista
  async getListInfo(): Promise<{ success: boolean; data?: MailchimpListInfo; message: string }> {
    if (this.isDevelopmentMode) {
      return {
        success: true,
        message: 'Información simulada',
        data: {
          id: 'dev_list',
          name: 'Bitravel Pre-Launch (Development)',
          member_count: 42
        }
      };
    }

    try {
      const response = await fetch(
        `${this.baseUrl}/lists/${this.audienceId}`,
        {
          headers: {
            'Authorization': `Basic ${btoa(`anystring:${this.apiKey}`)}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        return {
          success: true,
          data,
          message: 'Información obtenida exitosamente'
        };
      } else {
        const error = await response.json();
        return {
          success: false,
          message: error.detail || 'Error al obtener información de la lista'
        };
      }
    } catch (error) {
      return {
        success: false,
        message: 'Error de conexión'
      };
    }
  }

  // Método para obtener el estado del servicio
  getServiceStatus(): { 
    isDevelopmentMode: boolean; 
    hasCredentials: boolean;
    config: {
      apiKey: string;
      audienceId: string;
      serverPrefix: string;
      baseUrl: string;
    }
  } {
    return {
      isDevelopmentMode: this.isDevelopmentMode,
      hasCredentials: !this.apiKey.includes('YOUR_') && !this.audienceId.includes('YOUR_'),
      config: {
        apiKey: this.apiKey ? `${this.apiKey.slice(0, 8)}...${this.apiKey.slice(-4)}` : 'not_set',
        audienceId: this.audienceId,
        serverPrefix: this.serverPrefix,
        baseUrl: this.baseUrl
      }
    };
  }
}

// Exportar instancia singleton
export const mailchimpService = new MailchimpService();
export default mailchimpService;